import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Session } from "@supabase/supabase-js";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EODProvider } from "@/components/eod/EODProvider";
import { RoleProvider } from "@/contexts/RoleContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { TourProvider } from "@/contexts/TourContext";
import { supabase } from "@/integrations/supabase/client";

// Login Page
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";

// Client Pages
import Dashboard from "@/pages/Dashboard";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Support from "@/pages/Support";
import EODReports from "@/pages/EODReports";
import AccountSettings from "@/pages/settings/AccountSettings";
import NotificationsSettings from "@/pages/settings/NotificationsSettings";
import IntegrationsSettings from "@/pages/settings/IntegrationsSettings";
import TeamSettings from "@/pages/settings/TeamSettings";
import ClientDemosDownloads from "@/pages/client/DemosDownloads";
import ClientSales from "@/pages/client/Sales";
import ClientReports from "@/pages/client/ClientReports";
import SalesRevenue from "@/pages/client/SalesRevenue";
import PackageDetails from "@/pages/client/PackageDetails";
import Billing from "@/pages/client/Billing";
import DemoLibrary from "@/pages/client/DemoLibrary";
import TicketDetail from "@/pages/client/TicketDetail";
import Marketing from "@/pages/client/Marketing";
import ClientAttribution from "@/pages/client/Attribution";
import ClientOnboarding from "@/pages/client/Onboarding";

// Admin Pages
import CommandCenter from "@/pages/admin/CommandCenter";
import VentureHillsOverview from "@/pages/admin/VentureHillsOverview";
import OnboardingProcess from "@/pages/admin/OnboardingProcess";
import Pipeline from "@/pages/admin/Pipeline";
import AdminDashboards from "@/pages/admin/AdminDashboards";
import SalesOperations from "@/pages/admin/SalesOperations";
import AdminDemosDownloads from "@/pages/admin/AdminDemosDownloads";
import Packages from "@/pages/admin/Packages";
import AdminReports from "@/pages/admin/AdminReports";
import SupportTickets from "@/pages/admin/SupportTickets";
import AdminTicketDetail from "@/pages/admin/AdminTicketDetail";
import RolesPermissions from "@/pages/admin/RolesPermissions";
import AdminAttribution from "@/pages/admin/Attribution";
import AdminNotificationSettings from "@/pages/admin/AdminNotificationSettings";
import AdminDemoLibrary from "@/pages/admin/DemoLibrary";
import IntegrationHealth from "@/pages/admin/IntegrationHealth";
import CompanyProfileSettings from "@/pages/settings/CompanyProfileSettings";

// Team (setter/closer) Pages
import SetterHome from "@/pages/setter/Home";
import SetterLeads from "@/pages/setter/Leads";
import SetterSettings from "@/pages/setter/Settings";
import CloserHome from "@/pages/closer/Home";
import CloserFathom from "@/pages/closer/Fathom";
import CloserDemoLibrary from "@/pages/closer/DemoLibrary";
import CloserSettings from "@/pages/closer/Settings";

import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

// Guard: redirect to login if no valid session
function AuthGuard({ session }: { session: Session | null }) {
  if (!session) return <Navigate to="/login" replace />;
  return <Outlet />;
}

// Guard: admin-only routes — redirect clients to their dashboard
function AdminGuard({ session }: { session: Session | null }) {
  const accountType = session?.user?.user_metadata?.account_type;
  if (accountType !== "admin") return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

// Guard: client routes — allow clients and admins who are impersonating a client.
// Team users (setter/closer) bounce to their role home (Pitfall 4) instead of Command Center.
function ClientGuard({ session }: { session: Session | null }) {
  const accountType = session?.user?.user_metadata?.account_type;
  // Only a real admin session can use impersonation — prevents sessionStorage spoofing
  const isImpersonating = accountType === "admin" && !!sessionStorage.getItem("admin_impersonating");
  if (accountType !== "client" && !isImpersonating) {
    const role = session?.user?.user_metadata?.role;
    if (accountType === "admin") return <Navigate to="/admin/command-center" replace />;
    if (accountType === "team" && role === "setter") return <Navigate to="/setter/home" replace />;
    if (accountType === "team" && role === "closer") return <Navigate to="/closer/home" replace />;
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

// Guard: team-role routes — setter/closer portals. Cross-role visits bounce to own home (T-16-05).
function TeamGuard({ session, allowedRole }: { session: Session | null; allowedRole: 'setter' | 'closer' }) {
  const accountType = session?.user?.user_metadata?.account_type;
  const role = session?.user?.user_metadata?.role;
  if (accountType !== "team") return <Navigate to="/login" replace />;
  if (role !== allowedRole) {
    return <Navigate to={role === "closer" ? "/closer/home" : "/setter/home"} replace />;
  }
  return <Outlet />;
}

function AppRoutes({ session }: { session: Session | null }) {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* All authenticated routes */}
      <Route element={<AuthGuard session={session} />}>
        <Route element={<DashboardLayout />}>

          {/* Client-only routes */}
          <Route element={<ClientGuard session={session} />}>
            <Route path="/client/onboarding" element={<ClientOnboarding />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/demos-downloads" element={<ClientDemosDownloads />} />
            <Route path="/demos" element={<ClientDemosDownloads />} />
            <Route path="/sales" element={<ClientSales />} />
            <Route path="/sales-revenue" element={<SalesRevenue />} />
            <Route path="/marketing" element={<Marketing />} />
            <Route path="/attribution" element={<ClientAttribution />} />
            <Route path="/package-details" element={<PackageDetails />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/demo-library" element={<DemoLibrary />} />
            <Route path="/reports" element={<ClientReports />} />
            <Route path="/support" element={<Support />} />
            <Route path="/support/ticket/:ticketId" element={<TicketDetail />} />
            <Route path="/eod-reports" element={<EODReports />} />
            <Route path="/settings" element={<Settings />}>
              <Route index element={<Navigate to="/settings/account" replace />} />
              <Route path="account" element={<AccountSettings />} />
              <Route path="company" element={<CompanyProfileSettings />} />
              <Route path="notifications" element={<NotificationsSettings />} />
              <Route path="integrations" element={<IntegrationsSettings />} />
              <Route path="team" element={<TeamSettings />} />
            </Route>
          </Route>

          {/* Admin-only routes */}
          <Route element={<AdminGuard session={session} />}>
            <Route path="/admin/command-center" element={<CommandCenter />} />
            <Route path="/admin/overview" element={<VentureHillsOverview />} />
            <Route path="/admin/onboarding" element={<OnboardingProcess />} />
            <Route path="/admin/pipeline" element={<Pipeline />} />
            <Route path="/admin/dashboards" element={<AdminDashboards />} />
            <Route path="/admin/sales-ops" element={<SalesOperations />} />
            <Route path="/admin/demos-downloads" element={<AdminDemosDownloads />} />
            <Route path="/admin/packages" element={<Packages />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/integration-health" element={<IntegrationHealth />} />
            <Route path="/admin/support-tickets" element={<SupportTickets />} />
            <Route path="/admin/support-tickets/:ticketId" element={<AdminTicketDetail />} />
            <Route path="/admin/roles-permissions" element={<RolesPermissions />} />
            <Route path="/admin/attribution" element={<AdminAttribution />} />
            <Route path="/admin/notification-settings" element={<AdminNotificationSettings />} />
            <Route path="/admin/demo-library" element={<AdminDemoLibrary />} />
          </Route>

          {/* Team: setter-only routes */}
          <Route element={<TeamGuard session={session} allowedRole="setter" />}>
            <Route path="/setter/home" element={<SetterHome />} />
            <Route path="/setter/leads" element={<SetterLeads />} />
            <Route path="/setter/settings" element={<SetterSettings />} />
          </Route>

          {/* Team: closer-only routes */}
          <Route element={<TeamGuard session={session} allowedRole="closer" />}>
            <Route path="/closer/home" element={<CloserHome />} />
            <Route path="/closer/fathom" element={<CloserFathom />} />
            <Route path="/closer/demo-library" element={<CloserDemoLibrary />} />
            <Route path="/closer/settings" element={<CloserSettings />} />
          </Route>

        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => {
  // undefined = still checking, null = no session, Session = authenticated
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    // Check existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session ?? null);
      if (!session) {
        // Clear any stale localStorage left from a previous session
        localStorage.removeItem("onboarding_complete");
      }
    });

    // Keep auth state in sync with Supabase session
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session ?? null);
      if (!session) {
        // Session ended — clear all auth-related storage
        localStorage.removeItem("onboarding_complete");
        localStorage.removeItem("user_role");
        localStorage.removeItem("admin_user");
        localStorage.removeItem("account_type");
        localStorage.removeItem("active_client_id");
        localStorage.removeItem("client_package");
        localStorage.removeItem("client_services");
        sessionStorage.clear();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Don't render routes until we know auth state — prevents flash
  if (session === undefined) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <TourProvider>
        <TooltipProvider delayDuration={0}>
          <Toaster />
          <Sonner />
          <ErrorBoundary>
            <BrowserRouter>
              <RoleProvider session={session}>
                <EODProvider>
                  <AppRoutes session={session} />
                </EODProvider>
              </RoleProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </TooltipProvider>
      </TourProvider>
    </QueryClientProvider>
  );
};

export default App;
